import React, { useEffect, useState } from "react";
import templates from "@shared/icons/templates.svg";
import projectsIcon from "@shared/icons/projects.svg";
import programsIcon from "@shared/icons/handshake.svg";
import assistantIcon from "@shared/icons/assistant.svg";
import { Link, useLocation, useParams } from "react-router-dom";
import { useUser } from "@services/UserContext";
import { useSidebar } from "@services/SidebarContext";
import SidebarSkeleton from "@shared/components/Sidebar/SidebarSkeleton";
import { useFlags } from "launchdarkly-react-client-sdk";
import ConversationList from "./components/ConversationList";
import ProgramList from "./components/ProgramList";
import { useAppContext } from "@shared/context/AppContext";

const Sidebar: React.FC<{ isSidebarDropdownOpened: boolean }> = (
  isSidebarDropdownOpened
) => {
  const { user } = useUser();
  const { projects, programs, conversations, loading } = useAppContext();

  const [chatsVisible, setChatsVisible] = useState<boolean>(false);
  const [programsVisible, setProgramsVisible] = useState<boolean>(false);
  const sidebarContext = useSidebar();

  const flags = useFlags();
  console.log("Flags in Sidebar:", flags);

  const { evaluation: evaluationFlag } = useFlags();

  const { projectId } = useParams();

  const handleExpandProjects = () => {
    const willOpen = !sidebarContext.isSidebarDropdownOpened;
    if (willOpen) {
      setProgramsVisible(false);
      setChatsVisible(false);
    }
    sidebarContext.setIsSidebarDropdownOpened(willOpen);
  };

  const handleExpandAssistant = () => {
    if (!chatsVisible) {
      sidebarContext.setIsSidebarDropdownOpened(false);
      setProgramsVisible(false);
    }
    setChatsVisible(!chatsVisible);
  };

  const handleExpandPrograms = () => {
    if (!programsVisible) {
      sidebarContext.setIsSidebarDropdownOpened(false);
      setChatsVisible(false);
    }
    setProgramsVisible(!programsVisible);
  };

  const location = useLocation();

  const isProgramsActive =
    location.pathname.startsWith("/programs") ||
    location.pathname.startsWith("/create-program");

  const isTemplatesActive =
    location.pathname.startsWith("/templates") ||
    location.pathname.startsWith("/create-template") ||
    /^\/template\/\d+\/edit/.test(location.pathname);

  const isProjectsActive =
    location.pathname === "/projects" ||
    location.pathname.startsWith("/create-project");

  const isAssitantActive =
    location.pathname === "/assistant" || location.pathname === "/home";

  const isEvaluationActive = location.pathname === "/evaluation";

  const isManageMemberActive = location.pathname === "/manage-members";

  const isTakeTourActive = location.pathname === "/take-tour";

  const path = location.pathname;

  useEffect(() => {
    // Assistant conversations: /assistant/conversation/:id
    if (/^\/assistant\/conversation\/[^/]+/.test(path)) {
      setChatsVisible(true);
      setProgramsVisible(false);
      sidebarContext.setIsSidebarDropdownOpened(false);
      return;
    }

    // Project detail: /project/:id
    if (/^\/project\/[^/]+/.test(path)) {
      sidebarContext.setIsSidebarDropdownOpened(true);
      setChatsVisible(false);
      setProgramsVisible(false);
      return;
    }

    // Program detail: /programs/:id or /program/:id
    if (/^\/programs?\/[^/]+/.test(path)) {
      setProgramsVisible(true);
      setChatsVisible(false);
      sidebarContext.setIsSidebarDropdownOpened(false);
      return;
    }

    // For all other routes, don't force-close anything—
    // leave the user's last toggles as-is.
  }, [path]);

  const ACTIVE_COLOR = "#F2F4F7";

  if (loading) {
    return <SidebarSkeleton />;
  }
  return (
    <>
      <div className="flex text-sm 2xl:text-base">
        <div className="flex flex-col max-h-[calc(100vh-4.75rem)] h-[calc(100vh-4.75rem)] py-5 px-6 bg-[#FFFFFF] shadow w-72 2xl:w-80 flex-shrink-0 space-y-1">
          {/* Assistant */}

          <div className="flex-col flex scrollbar-hide relative overflow-auto min-h-12">
            <div className="flex flex-col h-full">
              <div
                className="cursor-pointer assistant-header"
                onClick={handleExpandAssistant}
              >
                <div
                  className={`flex items-center justify-between p-2 rounded-md hover:bg-[${ACTIVE_COLOR}] h-12 ${
                    isAssitantActive ? `bg-[${ACTIVE_COLOR}]` : ""
                  }`}
                >
                  <Link
                    to="/assistant?new=1"
                    className="flex items-center space-x-3 h-12 pr-8"
                  >
                    <img
                      alt="Assistant"
                      src={assistantIcon}
                      className="w-6 h-6"
                    />
                    <span>Assistant</span>
                  </Link>

                  {conversations.length > 0 && (
                    <div className="flex items-center space-x-1">
                      <button
                        className={`flex items-center justify-center rounded-md p-3 hover:bg-[${ACTIVE_COLOR}]`}
                      >
                        <svg
                          className="h-3 w-3"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 10 6"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            d={chatsVisible ? "M0 6h10L5 0z" : "M0 0h10L5 6z"}
                          ></path>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {chatsVisible && conversations.length > 0 && (
                <div className="flex-1 overflow-y-auto custom-scroll mb-4">
                  <ConversationList
                    conversations={conversations}
                    activeColor={ACTIVE_COLOR}
                  />
                </div>
              )}
            </div>
            {sidebarContext.sidebarDisabled && (
              <div className="absolute top-0 left-0 w-full h-full opacity-70 bg-[#F9FAFB] cursor-not-allowed"></div>
            )}
          </div>

          {/* Programs Link */}
          <div className="flex-col flex scrollbar-hide relative overflow-auto min-h-12">
            <div className="flex flex-col h-full">
              <div
                className="cursor-pointer assistant-header"
                onClick={handleExpandPrograms}
              >
                <div
                  className={`flex items-center justify-between p-2 rounded-md hover:bg-[${ACTIVE_COLOR}] h-12 ${
                    isProgramsActive ? `bg-[${ACTIVE_COLOR}]` : ""
                  }`}
                >
                  <Link
                    to="/programs"
                    className="flex items-center space-x-3 h-12 pr-8"
                  >
                    <img
                      alt="Programs"
                      src={programsIcon}
                      className="w-6 h-6"
                    />
                    <span>Programs</span>
                  </Link>

                  {programs.length > 0 && (
                    <div className="flex items-center space-x-1">
                      <button
                        className={`flex items-center justify-center rounded-md p-3 hover:bg-[${ACTIVE_COLOR}]`}
                      >
                        <svg
                          className="h-3 w-3"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 10 6"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            d={
                              programsVisible ? "M0 6h10L5 0z" : "M0 0h10L5 6z"
                            }
                          ></path>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {programsVisible && programs.length > 0 && (
                <div className="flex-1 overflow-y-auto custom-scroll mb-4">
                  <ProgramList programs={programs} activeColor={ACTIVE_COLOR} />
                </div>
              )}
            </div>
          </div>

          {/* Templates Link */}
          <div className="h-12">
            <Link
              to="/templates"
              className={`flex items-center justify-between p-2 rounded-md hover:bg-[${ACTIVE_COLOR}] h-12 ${
                isTemplatesActive ? `bg-[${ACTIVE_COLOR}]` : ""
              }`}
            >
              <div className="flex items-center space-x-3">
                <img alt="Templates" src={templates} className="w-6 h-6" />
                <span>Templates</span>
              </div>
            </Link>
          </div>

          {/* Projects and Dropdown Button */}
          <div
            className={`flex-col flex relative overflow-auto min-h-12 ${
              chatsVisible && "max-h-32 lg:max-h-64"
            }`}
          >
            <div className="flex flex-col h-full">
              <div onClick={handleExpandProjects} className="cursor-pointer">
                <div
                  className={`flex items-center justify-between p-2 rounded-md hover:bg-[${ACTIVE_COLOR}] h-12 ${
                    isProjectsActive ? `bg-[${ACTIVE_COLOR}]` : ""
                  }`}
                >
                  <Link
                    to="/projects"
                    className="flex items-center space-x-3 h-12"
                  >
                    <img
                      alt="Projects"
                      src={projectsIcon}
                      className="w-6 h-6"
                    />
                    <span>Projects</span>
                  </Link>

                  {projects.length > 0 && (
                    <button
                      className={`flex items-center justify-center rounded-md p-3 hover:bg-[${ACTIVE_COLOR}]`}
                    >
                      <svg
                        className="h-3 w-3"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 10 6"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          d={
                            sidebarContext.isSidebarDropdownOpened
                              ? "M0 6h10L5 0z"
                              : "M0 0h10L5 6z"
                          }
                        ></path>
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {sidebarContext.isSidebarDropdownOpened &&
                projects.length > 0 && (
                  <div className="flex-1 overflow-y-auto custom-scroll space-y-2 mb-4">
                    <ul className="space-y-2 mt-1 font-light pl-4">
                      {projects.map((project, index) => (
                        <li key={index}>
                          <Link
                            to={`/project/${project.id}`}
                            className={`flex items-center p-2 space-x-3 rounded-md hover:bg-[${ACTIVE_COLOR}] ${
                              projectId === String(project.id)
                                ? `bg-[${ACTIVE_COLOR}]`
                                : ""
                            }`}
                          >
                            <span className="w-6 h-6">
                              {project.icon.length > 0 ? project.icon : "⭐"}
                            </span>
                            <span>{project.name}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>
          </div>

          {/* Evaluation Link */}
          {evaluationFlag && (
            <div className="h-12">
              <Link
                to="/evaluation"
                className={`flex items-center justify-between p-2 rounded-md hover:bg-[${ACTIVE_COLOR}] h-12 ${
                  isEvaluationActive ? `bg-[${ACTIVE_COLOR}]` : ""
                }`}
              >
                <div className="flex items-center space-x-3">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  <span>Evaluation</span>
                </div>
              </Link>
            </div>
          )}

          {/* spacer */}
          <div className="flex-1"></div>
          {/* User Info Section */}
          <div className="h-12">
            <Link
              to="/take-tour"
              className={`flex items-center justify-between p-2 rounded-md hover:bg-[${ACTIVE_COLOR}] h-12 ${
                isTakeTourActive ? `bg-[${ACTIVE_COLOR}]` : ""
              }`}
            >
              <div className="flex items-center space-x-3">
                <img
                  alt="Templates"
                  src={"/take-tour.svg"}
                  className="w-6 h-6"
                />
                <span>Take a Tour</span>
              </div>
            </Link>
          </div>
          {user?.company_relation?.is_admin && (
            <div className=" mt-auto mb-0 pb-0 ">
              <Link
                to="/manage-members"
                className={`flex items-center justify-between p-2 rounded-md hover:bg-[${ACTIVE_COLOR}] h-12 mb-3 ${
                  isManageMemberActive ? `bg-[${ACTIVE_COLOR}]` : ""
                }`}
              >
                <div className="flex items-center space-x-3">
                  <img alt="Members" src={"/users.svg"} className="w-6 h-6" />
                  <span>Manage Members</span>
                </div>
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
